import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const INITIAL_FORM = {
  portfolioName: '',
  portfolioDescription: '',
  totalApplications: '',
  dominoServerVersion: '',
  migrateDatabaseData: '',
  dbAnalysisRequested: false,
  targetPlatform: '',
  priority: '',
  hasMigrationTeam: '',
  preferredEngagement: '',
  desiredTimeline: '',
  hasComplianceRequirements: '',
  complianceDetails: '',
  sharedDatabase: '',
  allUiApps: '',
  hasDependencies: '',
  dependencyDetails: '',
  hasMailEnabledApps: '',
  additionalNotes: '',
};

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@rivit.ca';
const SUPPORT_PHONE = process.env.REACT_APP_SUPPORT_PHONE || '647.930.1711';

export default function MassImportPage() {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [zipFile, setZipFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Validation overlay
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Access code modal
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);

  const remaining = Math.max(0, (user.submissionLimit || 1) - (user.submissionsUsed || 0));
  const massImportAllowed = (user.submissionLimit || 1) > 1;

  // Auto-show access code modal if mass import not allowed or no remaining submissions
  useEffect(() => {
    if (!massImportAllowed || remaining <= 0) {
      setShowAccessCodeModal(true);
    }
  }, [massImportAllowed, remaining]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'zip') {
      addToast('Mass import requires a .zip file containing your DXL exports.', 'error');
      e.target.value = '';
      setZipFile(null);
      setFileValid(false);
      return;
    }

    // Show validation overlay and validate on server
    setZipFile(file);
    setFileValid(false);
    setValidating(true);
    setValidationError('');

    try {
      const formData = new FormData();
      formData.append('dxlFile', file);

      const res = await fetch('/api/submissions/validate', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      if (!res.ok || !data.valid) {
        setValidationError(data.error || 'File validation failed.');
        setZipFile(null);
        setFileValid(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setFileValid(true);
        if (data.fileCount) {
          addToast(`ZIP validated — ${data.fileCount} DXL file(s) found.`, 'success');
        }
      }
    } catch (err) {
      setValidationError('Could not validate file. Please try again.');
      setZipFile(null);
      setFileValid(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setValidating(false);
    }
  };

  const validate = () => {
    if (!form.portfolioName.trim()) return 'Portfolio name is required.';
    if (!form.totalApplications || Number(form.totalApplications) < 1) return 'Enter the approximate number of applications.';
    if (!form.dominoServerVersion) return 'Please select Domino Server Version.';
    if (!form.migrateDatabaseData) return 'Please indicate whether you want to migrate the database data.';
    if (!form.targetPlatform) return 'Please select a target platform.';
    if (!form.priority) return 'Please select a priority level.';
    if (!form.hasMigrationTeam) return 'Please select whether your organization has a migration team.';
    if (!form.preferredEngagement) return 'Please select a Migration Delivery Model.';
    if (!form.desiredTimeline) return 'Please select a desired migration timeline.';
    if (!form.hasComplianceRequirements) return 'Please indicate if there are compliance requirements.';
    if (form.hasComplianceRequirements === 'Yes' && !form.complianceDetails.trim()) {
      return 'Please describe compliance requirements.';
    }
    if (!form.sharedDatabase) return 'Please answer whether applications share a common database.';
    if (!form.allUiApps) return 'Please answer whether all applications are user-facing.';
    if (!form.hasDependencies) return 'Please answer whether applications depend on each other.';
    if (form.hasDependencies === 'Yes' && !form.dependencyDetails.trim()) {
      return 'Please describe the application dependencies.';
    }
    if (!form.hasMailEnabledApps) return 'Please answer whether any applications are mail-enabled.';
    if (!zipFile || !fileValid) return 'Please upload a valid ZIP file containing your DXL exports.';
    return null;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      addToast(error, 'error');
      return;
    }

    // Check mass import eligibility
    if (!massImportAllowed || remaining <= 0) {
      setShowAccessCodeModal(true);
      return;
    }

    setShowConfirm(true);
  };

  const handleRedeemCode = async () => {
    if (!accessCode.trim()) {
      addToast('Please enter an access code.', 'error');
      return;
    }
    setRedeemingCode(true);
    try {
      const res = await fetch('/api/submissions/redeem-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: accessCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      setAccessCode('');
      setShowAccessCodeModal(false);
      await refreshUser();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setRedeemingCode(false);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setShowConfirm(false);
    setSubmitting(true);

    try {
      const payload = {
        ...form,
        organizationName: user.organizationName,
        contactName: user.fullName,
        contactEmail: user.email,
        totalApplications: Number(form.totalApplications),
        submissionType: 'mass-import',
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));
      formData.append('dxlFile', zipFile);

      const res = await fetch('/api/submissions/mass-import', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.limitReached || data.massImportBlocked) {
          setShowAccessCodeModal(true);
          return;
        }
        throw new Error(data.message);
      }

      await refreshUser();
      addToast('Mass import submitted successfully!', 'success');
      navigate('/dashboard');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="submit-page">
      <div className="submit-header">
        <div className="submit-header-row">
          <div>
            <h1>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              Mass Import
            </h1>
            <p className="submit-subtitle">Submit multiple Lotus Notes applications in a single ZIP archive</p>
          </div>
        </div>
      </div>

      {/* Submission Quota Banner */}
      <div className={`quota-banner ${!massImportAllowed || remaining <= 0 ? 'quota-banner-warn' : ''}`}>
        <div className="quota-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {!massImportAllowed ? (
            <span>Mass import requires an access code. Enter one to unlock this feature.</span>
          ) : (
            <span>
              <strong>{remaining}</strong> submission{remaining !== 1 ? 's' : ''} remaining
              <span className="quota-detail"> ({user.submissionsUsed || 0} used of {user.submissionLimit || 1})</span>
            </span>
          )}
        </div>
        <button className="btn btn-sm btn-outline" onClick={() => setShowAccessCodeModal(true)}>
          Enter Access Code
        </button>
      </div>

      <form onSubmit={handlePreSubmit} className="submit-form">
        {/* Contact Info */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Contact Information
          </h3>
          <p className="form-card-desc">Auto-filled from your profile</p>
          <div className="form-row">
            <div className="form-group">
              <label>Organization Name</label>
              <input type="text" value={user.organizationName} disabled />
            </div>
            <div className="form-group">
              <label>Contact Name</label>
              <input type="text" value={user.fullName} disabled />
            </div>
            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" value={user.email} disabled />
            </div>
          </div>
        </div>

        {/* Domino Environment */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
            Domino Environment
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dominoServerVersion">Domino Server Version *</label>
              <select id="dominoServerVersion" name="dominoServerVersion" value={form.dominoServerVersion} onChange={handleChange}>
                <option value="">Select version...</option>
                <option value="v9.x">v9.x</option>
                <option value="v10.x">v10.x</option>
                <option value="v11.x">v11.x</option>
                <option value="v12.x">v12.x</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="migrateDatabaseData">Do you want to migrate the database as well ? *</label>
              <select id="migrateDatabaseData" name="migrateDatabaseData" value={form.migrateDatabaseData} onChange={handleChange}>
                <option value="">Select an option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
          </div>
          <label className="checkbox-card">
            <input
              type="checkbox"
              name="dbAnalysisRequested"
              checked={form.dbAnalysisRequested}
              onChange={handleChange}
            />
            <div className="checkbox-card-content">
              <span className="checkbox-card-title">
                I also want a database analysis.
                <span className="inline-muted-hint"> (This is performed as a separate analysis engagement.)</span>
              </span>
            </div>
          </label>
        </div>

        {/* Portfolio Overview */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            Portfolio Overview
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="portfolioName">Portfolio Name *</label>
              <input id="portfolioName" name="portfolioName" type="text" placeholder="e.g., HR Department Applications" value={form.portfolioName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="totalApplications">Approx. Number of Applications *</label>
              <input id="totalApplications" name="totalApplications" type="number" min="1" placeholder="e.g., 25" value={form.totalApplications} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="portfolioDescription">Portfolio Description</label>
            <textarea id="portfolioDescription" name="portfolioDescription" rows="3" placeholder="Describe the collection of applications — what department/function they serve, common patterns..." value={form.portfolioDescription} onChange={handleChange} />
          </div>
        </div>

        {/* Migration Preferences */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Migration Preferences
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="targetPlatform">Target Platform *</label>
              <select id="targetPlatform" name="targetPlatform" value={form.targetPlatform} onChange={handleChange}>
                <option value="">Select platform...</option>
                <option value="Revive React + Java [spring] + Mysql | H2">Revive React + Java [spring] + Mysql | H2</option>
                <option value="(BETA) Revive React + Java [spring] + Mongo" disabled title="Releasing soon">(BETA) Revive React + Java [spring] + Mongo</option>
                <option value="(BETA) Revive React + .Net + Mongo" disabled title="Releasing soon">(BETA) Revive React + .Net + Mongo</option>
                <option value="(BETA) Revive React + .Net + Mysql | H2" disabled title="Releasing soon">(BETA) Revive React + .Net + Mysql | H2</option>
                <option value="No preference">No preference</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority *</label>
              <select id="priority" name="priority" value={form.priority} onChange={handleChange}>
                <option value="">Select priority...</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="hasMigrationTeam">Does your organization have a migration team? *</label>
            <select id="hasMigrationTeam" name="hasMigrationTeam" value={form.hasMigrationTeam} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="No - Rivit handles everything">No - Rivit handles everything</option>
              <option value="Yes - internal team">Yes - internal team</option>
              <option value="Partially - collaborative approach">Partially - collaborative approach</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="preferredEngagement">Migration Delivery Model *</label>
            <select id="preferredEngagement" name="preferredEngagement" value={form.preferredEngagement} onChange={handleChange}>
              <option value="">Select engagement...</option>
              <option value="Full-Service - Rivit handles everything">Full-Service - Rivit handles everything</option>
              <option value="Guided - Rivit advises your team executes">Guided - Rivit advises your team executes</option>
              <option value="Collaborative - Joint effort">Collaborative - Joint effort</option>
              <option value="Assessment Only - Just the analysis report">Assessment Only - Just the analysis report</option>
            </select>
          </div>
        </div>

        {/* Timeline & Compliance */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Timeline &amp; Compliance
          </h3>
          <div className="form-group">
            <label htmlFor="desiredTimeline">Desired migration timeline *</label>
            <select id="desiredTimeline" name="desiredTimeline" value={form.desiredTimeline} onChange={handleChange}>
              <option value="">Select timeline...</option>
              <option value="ASAP">ASAP</option>
              <option value="Within 3 months">Within 3 months</option>
              <option value="Within 6 months">Within 6 months</option>
              <option value="Within 12 months">Within 12 months</option>
              <option value="No rush - planning phase">No rush - planning phase</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="hasComplianceRequirements">Are there regulatory or compliance requirements for this data? *</label>
            <select id="hasComplianceRequirements" name="hasComplianceRequirements" value={form.hasComplianceRequirements} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          {form.hasComplianceRequirements === 'Yes' && (
            <div className="form-group">
              <label htmlFor="complianceDetails">Compliance requirements details</label>
              <textarea id="complianceDetails" name="complianceDetails" rows="2" placeholder="Describe regulatory or compliance requirements..." value={form.complianceDetails} onChange={handleChange} />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="additionalNotes">Additional Notes</label>
            <textarea id="additionalNotes" name="additionalNotes" rows="2" placeholder="Any additional context or requirements..." value={form.additionalNotes} onChange={handleChange} />
          </div>
        </div>

        {/* Architecture & Dependencies */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
            Architecture &amp; Dependencies
          </h3>
          <div className="form-group">
            <label htmlFor="sharedDatabase">Do these applications share a common database? *</label>
            <select id="sharedDatabase" name="sharedDatabase" value={form.sharedDatabase} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes - shared database">Yes - shared database</option>
              <option value="No - each has its own">No - each has its own</option>
              <option value="Mixed">Mixed</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="allUiApps">Are all applications user-facing (UI)? *</label>
            <select id="allUiApps" name="allUiApps" value={form.allUiApps} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes - all UI apps">Yes - all UI apps</option>
              <option value="No - some are backend/agent-only">No - some are backend/agent-only</option>
              <option value="Mixed">Mixed</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="hasDependencies">Do any applications link to or depend on each other? *</label>
            <select id="hasDependencies" name="hasDependencies" value={form.hasDependencies} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          {form.hasDependencies === 'Yes' && (
            <div className="form-group">
              <label htmlFor="dependencyDetails">Dependency details</label>
              <textarea id="dependencyDetails" name="dependencyDetails" rows="2" placeholder="Describe dependencies briefly..." value={form.dependencyDetails} onChange={handleChange} />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="hasMailEnabledApps">Are any of these applications mail-enabled (mail-in databases)? *</label>
            <select id="hasMailEnabledApps" name="hasMailEnabledApps" value={form.hasMailEnabledApps} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
        </div>

        {/* File Upload */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Portfolio Upload
          </h3>
          <p className="form-card-desc">Upload a ZIP archive containing all DXL exports (.dxl or .xml files only)</p>
          <div
            className={`file-drop-zone ${zipFile && fileValid ? 'file-drop-zone-active' : ''} ${validationError ? 'file-drop-zone-error' : ''}`}
            onClick={() => !validating && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {zipFile && fileValid ? (
              <div className="file-selected">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  <polyline points="9 15 12 18 15 15"/>
                </svg>
                <span className="file-name">{zipFile.name}</span>
                <span className="file-size">{(zipFile.size / (1024 * 1024)).toFixed(2)} MB</span>
                <span className="file-valid-badge">✓ Validated</span>
              </div>
            ) : (
              <div className="file-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>Click to upload your ZIP archive</span>
                <span className="file-hint">.zip files containing DXL/XML exports (up to 50MB)</span>
              </div>
            )}
          </div>
          {validationError && (
            <div className="file-validation-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <span style={{ whiteSpace: 'pre-line' }}>{validationError}</span>
            </div>
          )}
        </div>

        <div className="submit-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || validating}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><polyline points="17 8 12 3 7 8" style={{transform: 'translate(0, 6px)'}}/></svg>
            {submitting ? 'Submitting...' : 'Submit Portfolio'}
          </button>
        </div>
      </form>

      {/* Validation Overlay */}
      {validating && (
        <div className="dialog-overlay validation-overlay">
          <div className="validation-modal">
            <div className="spinner" />
            <h3>Validating ZIP file...</h3>
            <p>Checking all files inside for valid DXL/XML content</p>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="dialog-overlay" onClick={() => setShowConfirm(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h3 className="dialog-title">Confirm Mass Import</h3>
            <p className="dialog-message">
              You are about to submit <strong>{form.portfolioName}</strong> ({form.totalApplications} applications) for migration analysis. This will upload your ZIP archive for review.
            </p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowConfirm(false)}>Go Back</button>
              <button className="btn btn-primary" onClick={handleSubmit}>Confirm &amp; Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Access Code Modal */}
      {showAccessCodeModal && (
        <div className="dialog-overlay" onClick={() => { if (massImportAllowed && remaining > 0) setShowAccessCodeModal(false); }}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 className="dialog-title">{!massImportAllowed ? 'Access Code Required' : 'Submission Limit Reached'}</h3>
            <p className="dialog-message">
              {!massImportAllowed
                ? 'Mass import submissions require an access code. Please enter one below to unlock this feature.'
                : "You've used all your submissions. Please enter an access code to continue."}
            </p>
            <p className="dialog-message" style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>
              Contact us to get an access code: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              {SUPPORT_PHONE && <> or call <a href={`tel:${SUPPORT_PHONE.replace(/\./g, '')}`}>{SUPPORT_PHONE}</a></>}
            </p>
            <div className="coupon-input-row">
              <input
                type="text"
                placeholder="Enter access code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="coupon-input"
                autoFocus
              />
              <button
                className="btn btn-primary"
                onClick={handleRedeemCode}
                disabled={redeemingCode || !accessCode.trim()}
              >
                {redeemingCode ? 'Applying...' : 'Apply'}
              </button>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => { setShowAccessCodeModal(false); navigate('/dashboard'); }}>Back to Dashboard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
